import { useSelector } from 'react-redux';
import Button, { Color, Size } from 'lib/Button';
import Auth from 'modules/Auth';
import { PermissionsEnum } from 'consts/permissions';
import { Store } from 'components/common/types/Store.types';
import { CreativeStatus } from 'components/common/types/Creative.types';
import flagSvg from 'assets/icons/flag.svg';
import removeSvg from 'assets/icons/remove.svg';
import checkSvg from 'assets/icons/check.svg';
import crossSvg from 'assets/icons/close.svg';
import hourglassSvg from 'assets/icons/hourglass.svg';
import arrowRightSvg from 'assets/icons/arrow_right.svg';
import restoreSvg from 'assets/icons/restore.svg';
import { getIsReseller } from 'store/publisher/selectors';
import usePendoTracking from 'customHooks/usePendoTracking';
import { UsePreviewContentButtons } from '../../hooks/usePreviewContentButtons';

type PreviewContentButtonsProps = UsePreviewContentButtons & {
  openRejectModal: () => void;
  openDeleteModal: () => void;
  openRestoreModal: () => void;
};

const PreviewContentButtons: React.FC<PreviewContentButtonsProps> = ({
  openRejectModal,
  openDeleteModal,
  openRestoreModal,
  creativeStatus,
  isCreativeButtonDisabled,
  handleApproveCreative,
  handleFlagCreative,
  handlePendingApprovalCreative,
  handleSendForApproval,
  // eslint-disable-next-line sonarjs/cognitive-complexity
}) => {
  const { getPendoDataId } = usePendoTracking();
  const isReseller = useSelector(getIsReseller);
  const marketId = useSelector((state: Store) => state.creative.marketId);
  const dealId = useSelector((state: Store) => state.creative.dealId);
  const advertiser = useSelector((state: Store) => state.creative.advertiser);
  const brand = useSelector((state: Store) => state.creative.brand);
  const isCreativeDeleted = useSelector((state: Store) => state.creative.deleted);

  if (!marketId) {
    return null;
  }

  if (isReseller) {
    if (creativeStatus === CreativeStatus.PENDING || creativeStatus === CreativeStatus.SENT_FOR_APPROVAL) {
      return (
        <>
          <div className="mt-2">
            <Button
              onClick={handleSendForApproval}
              isDisabled={isCreativeButtonDisabled || dealId.length === 0}
              label="Send for approval"
              svg={arrowRightSvg}
              size={Size.WIDE}
              classNames="p-3 flex justify-between"
              dataPendoId={getPendoDataId('Send for approval')}
              color={Color.PRIMARY}
            />
          </div>
          <div className="mt-2">
            <Button
              onClick={openRejectModal}
              color={Color.PRIMARY}
              size={Size.WIDE}
              classNames="p-3 flex justify-between"
              isDisabled={isCreativeButtonDisabled || creativeStatus === CreativeStatus.SENT_FOR_APPROVAL}
              label="Reject"
              svg={crossSvg}
              dataPendoId={getPendoDataId('Reject Button')}
            />
          </div>
        </>
      );
    }
    if (creativeStatus === CreativeStatus.REJECTED) {
      return (
        <>
          <div className="mt-2">
            {Auth.hasPermission(PermissionsEnum.CONTENT_REVERT_STATUS) && (
              <Button
                onClick={handlePendingApprovalCreative}
                color={Color.PRIMARY}
                isDisabled={isCreativeButtonDisabled}
                svg={hourglassSvg}
                label="Pending approval"
                size={Size.WIDE}
                classNames="p-3 flex justify-between"
                dataPendoId={getPendoDataId('Pending Approval Button')}
              />
            )}
          </div>
          <div className="mt-2">
            <Button
              onClick={openDeleteModal}
              svg={removeSvg}
              label="Delete creative"
              size={Size.WIDE}
              classNames="p-3 flex justify-between"
              color={Color.DANGER}
              dataPendoId={getPendoDataId('Delete Creative Button')}
            />
          </div>
        </>
      );
    }
    return null;
  }

  if (creativeStatus === CreativeStatus.APPROVED || creativeStatus === CreativeStatus.REJECTED) {
    return (
      <>
        <div>
          {Auth.hasPermission(PermissionsEnum.CONTENT_REVERT_STATUS) && (
            <Button
              onClick={handlePendingApprovalCreative}
              color={Color.PRIMARY}
              isDisabled={isCreativeButtonDisabled}
              svg={hourglassSvg}
              label="Pending approval"
              size={Size.WIDE}
              classNames="p-3 flex justify-between"
              dataPendoId={getPendoDataId('Pending Approval Button')}
            />
          )}
        </div>
        {!isCreativeDeleted &&
        Auth.hasPermission(PermissionsEnum.ADMINISTRATION_CREATIVE_DELETE) &&
        creativeStatus === CreativeStatus.REJECTED ? (
          <div className="mt-2">
            <Button
              onClick={openDeleteModal}
              label="Delete Creative"
              svg={removeSvg}
              size={Size.WIDE}
              classNames="p-3 flex justify-between"
              color={Color.DANGER}
              dataPendoId={getPendoDataId('Delete Creative Button')}
            />
          </div>
        ) : null}
        {isCreativeDeleted && Auth.hasPermission(PermissionsEnum.ADMINISTRATION_CREATIVE_RESTORE) ? (
          <div className="mt-2">
            <Button
              onClick={openRestoreModal}
              color={Color.PRIMARY}
              svg={restoreSvg}
              label="Restore Creative"
              size={Size.WIDE}
              classNames="p-3 flex justify-between"
              dataPendoId={getPendoDataId('Restore Creative Button')}
            />
          </div>
        ) : null}
      </>
    );
  }

  return (
    <>
      {Auth.hasPermission(PermissionsEnum.CONTENT_APPROVE) && (
        <div className="flex gap-2">
          <Button
            onClick={openRejectModal}
            color={Color.BLACK_AND_WHITE}
            isDisabled={isCreativeButtonDisabled}
            svg={crossSvg}
            label="Reject"
            size={Size.WIDE}
            classNames="p-3 flex justify-between"
            dataPendoId={getPendoDataId('Reject Button')}
            svgClassnames="text-pinkRed-500"
          />
          <Button
            onClick={handleApproveCreative}
            color={Color.BLACK_AND_WHITE}
            isDisabled={!advertiser?.code || !brand?.code || isCreativeButtonDisabled}
            svg={checkSvg}
            label="Approve"
            size={Size.WIDE}
            classNames="p-3 flex justify-between"
            dataPendoId={getPendoDataId('Approve Button')}
            svgClassnames="text-green-500"
          />
        </div>
      )}
      <div className="flex mt-2 gap-2">
        {creativeStatus !== CreativeStatus.FLAGGED && Auth.hasPermission(PermissionsEnum.CONTENT_FLAG) && (
          <Button
            onClick={handleFlagCreative}
            color={Color.PERSIAN_BLUE}
            isDisabled={isCreativeButtonDisabled}
            svg={flagSvg}
            label="Flag"
            size={Size.WIDE}
            classNames="p-3 flex justify-between"
            dataPendoId={getPendoDataId('Flag Button')}
          />
        )}
        {creativeStatus !== CreativeStatus.PENDING && Auth.hasPermission(PermissionsEnum.CONTENT_REVERT_STATUS) && (
          <Button
            onClick={handlePendingApprovalCreative}
            color={Color.PRIMARY}
            isDisabled={isCreativeButtonDisabled}
            svg={hourglassSvg}
            label="Pending approval"
            size={Size.WIDE}
            classNames="p-3 flex justify-between"
            dataPendoId={getPendoDataId('Pending Approval Button')}
          />
        )}
      </div>
      {!isCreativeDeleted &&
      Auth.hasPermission(PermissionsEnum.ADMINISTRATION_CREATIVE_DELETE) &&
      creativeStatus === CreativeStatus.PENDING ? (
        <div className="mt-2">
          <Button
            onClick={openDeleteModal}
            label="Delete Creative"
            svg={removeSvg}
            size={Size.WIDE}
            classNames="p-3 flex justify-between"
            dataPendoId={getPendoDataId('Delete Creative Button')}
            color={Color.DANGER}
          />
        </div>
      ) : null}
      {isCreativeDeleted && Auth.hasPermission(PermissionsEnum.ADMINISTRATION_CREATIVE_RESTORE) ? (
        <div className="mt-2">
          <Button
            onClick={openRestoreModal}
            color={Color.PRIMARY}
            svg={restoreSvg}
            label="Restore Creative"
            size={Size.WIDE}
            classNames="p-3 flex justify-between"
            dataPendoId={getPendoDataId('Restore Creative Button')}
          />
        </div>
      ) : null}
    </>
  );
};

export default PreviewContentButtons;
